/**
 * SupportTicketsPage — Phase 34.10.
 *
 * All support tickets across all customers with status filter. Polls every 30 s.
 */

import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtTime } from './CustomersDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface TicketSummary {
  id: string;
  customer_id: string;
  subject: string;
  body: string;
  language: string;
  status: string;
  response_body: string | null;
  chunks_used: number;
  created_at: number;
  resolved_at: number | null;
}

interface ListTicketsResponse {
  tickets: TicketSummary[];
  total: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

function fetchTickets(status?: string): Promise<ListTicketsResponse> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiFetch<ListTicketsResponse>(`/support/tickets${qs}`);
}

function useTickets(status?: string) {
  return useQuery({
    queryKey: ['support', 'tickets', status ?? 'all'],
    queryFn: () => fetchTickets(status),
    refetchInterval: 30_000,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

export const STATUS_COLORS: Record<string, string> = {
  open:      '#fbbf24',
  resolved:  '#34d399',
  escalated: '#f87171',
};

export function statusColor(s: string): string {
  return STATUS_COLORS[s] ?? 'var(--omega-ink-subtle)';
}

function FilterTab({ label, value, current, onSelect }: {
  label: string;
  value: string;
  current: string;
  onSelect: (v: string) => void;
}) {
  const active = value === current;
  return (
    <button
      onClick={() => onSelect(value)}
      style={{
        background: active ? 'rgba(125,211,252,0.12)' : 'rgba(255,255,255,0.05)',
        border: active ? '1px solid rgba(125,211,252,0.35)' : '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
        borderRadius: 4,
        color: active ? '#7dd3fc' : 'var(--omega-ink)',
        fontFamily: 'var(--omega-mono)',
        fontSize: '8.5pt',
        fontWeight: 600,
        padding: '5px 12px',
        cursor: 'pointer',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </button>
  );
}

function TicketRow({ t }: { t: TicketSummary }) {
  return (
    <Link
      to={`/omega/customers/support/${t.customer_id}/${t.id}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 0',
        borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <span style={{ flexShrink: 0, fontSize: '7.5pt', padding: '1px 6px', border: `1px solid ${statusColor(t.status)}`, color: statusColor(t.status), letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3 }}>
        {t.status}
      </span>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ fontSize: '9.5pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {t.subject || 'No subject'}
        </div>
        <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
          {t.body.slice(0, 100)}{t.body.length > 100 ? '…' : ''}
        </div>
      </div>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>{fmtTime(t.created_at)}</div>
        <Link to={`/omega/customers/${t.customer_id}`} onClick={e => e.stopPropagation()} style={{ fontSize: '8pt', color: '#7dd3fc', textDecoration: 'none' }}>
          {t.customer_id.slice(0, 8)}…
        </Link>
      </div>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function SupportTicketsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') ?? 'open';

  function setStatus(s: string) {
    setSearchParams(s === 'all' ? {} : { status: s });
  }

  const { data, isPending, isError } = useTickets(
    statusFilter === 'all' ? undefined : statusFilter
  );
  const tickets = data?.tickets ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Customers', to: '/omega/customers' },
          { label: 'Support Tickets' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Support Tickets
        </h1>
        <span style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)' }}>
          {data?.total ?? 0} total
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['open', 'resolved', 'escalated', 'all'].map(s => (
          <FilterTab
            key={s}
            label={s.charAt(0).toUpperCase() + s.slice(1)}
            value={s}
            current={statusFilter}
            onSelect={setStatus}
          />
        ))}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load tickets.</div>}
      {!isPending && !isError && tickets.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
          No {statusFilter === 'all' ? '' : statusFilter + ' '}tickets.
        </div>
      )}
      {tickets.map(t => <TicketRow key={t.id} t={t} />)}
    </div>
  );
}
